import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Quote,
  ChevronLeft,
  ChevronRight,
  Volume2,
  ThumbsUp,
  ThumbsDown,
} from "lucide-react";
import { quotesService, type Quote as QuoteType } from "@/services/quotesService";

interface HomeQuoteCardProps {
  quotes: QuoteType[];
  userId: string;
  authorFeedback: Record<string, "more" | "less">;
  onAuthorFeedbackChange: (feedback: Record<string, "more" | "less">) => void;
}

export function HomeQuoteCard({
  quotes,
  userId,
  authorFeedback,
  onAuthorFeedbackChange,
}: HomeQuoteCardProps) {
  const [quoteIndex, setQuoteIndex] = useState(0);
  const queryClient = useQueryClient();
  const currentQuote = quotes[quoteIndex] || quotes[0];

  const authorPreferenceMutation = useMutation({
    mutationFn: async ({ author, preference }: { author: string; preference: "more" | "less" }) => {
      await quotesService.updateAuthorPreference(userId, author, preference);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["author-preferences", userId] });
    },
  });

  const handleAuthorFeedback = (author: string, preference: "more" | "less") => {
    if (authorFeedback[author] === preference) return;
    onAuthorFeedbackChange({ ...authorFeedback, [author]: preference });
    authorPreferenceMutation.mutate({ author, preference });
  };

  const nextQuote = () => setQuoteIndex((prev) => (prev + 1) % quotes.length);
  const prevQuote = () => setQuoteIndex((prev) => (prev - 1 + quotes.length) % quotes.length);

  const speakQuote = () => {
    if ("speechSynthesis" in window && currentQuote) {
      const utterance = new SpeechSynthesisUtterance(
        `${currentQuote.text}. By ${currentQuote.author}`
      );
      utterance.rate = 0.9;
      speechSynthesis.speak(utterance);
    }
  };

  return (
    <Card className="border-none bg-gradient-to-r from-primary/5 via-purple-500/5 to-amber-500/5">
      <CardContent className="py-4">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={prevQuote}
            className="p-1 rounded-full hover:bg-muted/50 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-muted-foreground" />
          </button>
          <Quote className="w-8 h-8 text-primary/40 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-lg italic text-foreground/80">&ldquo;{currentQuote?.text}&rdquo;</p>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-sm text-muted-foreground">— {currentQuote?.author}</p>
              <Badge variant="outline" className="text-xs">
                {currentQuote?.expertise}
              </Badge>
              <div className="flex items-center gap-1 ml-2">
                <button
                  type="button"
                  onClick={() => currentQuote && handleAuthorFeedback(currentQuote.author, "more")}
                  className={`p-1.5 rounded-full transition-colors ${
                    authorFeedback[currentQuote?.author] === "more"
                      ? "bg-green-500/20 text-green-600"
                      : "hover:bg-muted/50 text-muted-foreground"
                  }`}
                  title="More quotes from this author"
                >
                  <ThumbsUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => currentQuote && handleAuthorFeedback(currentQuote.author, "less")}
                  className={`p-1.5 rounded-full transition-colors ${
                    authorFeedback[currentQuote?.author] === "less"
                      ? "bg-red-500/20 text-red-600"
                      : "hover:bg-muted/50 text-muted-foreground"
                  }`}
                  title="Less quotes from this author"
                >
                  <ThumbsDown className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={speakQuote}
              className="p-2 rounded-full hover:bg-muted/50 transition-colors"
              title="Listen to quote"
            >
              <Volume2 className="w-4 h-4 text-muted-foreground" />
            </button>
            <button
              type="button"
              onClick={nextQuote}
              className="p-1 rounded-full hover:bg-muted/50 transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>
        </div>
        <div className="flex justify-center gap-1 mt-3">
          {quotes.map((_, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setQuoteIndex(idx)}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx === quoteIndex ? "bg-primary" : "bg-muted-foreground/30"
              }`}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
